import React, { useEffect, useMemo, useState } from 'react';

import {
  AlertCircleIcon,
  Archive03Icon,
  AttachmentCircleIcon,
  CheckmarkCircle02Icon,
  Delete02Icon,
  RestoreBinIcon,
  UserIcon,
  WorkHistoryIcon,
} from '@hugeicons/core-free-icons';
import { DashboardSquare01Icon, Tag01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useAccessControl } from 'hooks/access-control';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useLocale } from 'hooks/locale';
import { useLocalStorage } from 'hooks/use-local-storage';
import AppPage from 'layouts/AppPage/AppPage';
import { Badge, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactGA from 'react-ga4';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { BrandDetails } from 'views/Company/tabs/Branding/BrandDetails';

import { BulkActionsBar } from './components/BulkActionsBar/BulkActionsBar';
import { CandidateHistory } from './components/CandidateHistory';
import { KnowledgeBaseCards } from './components/KnowledgeBaseCards/KnowledgeBaseCards';
import { Button } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';
import RolloverTooltip from 'components/RolloverTooltip/RolloverTooltip';
import { Table, useTableContext } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { getBrands, selectBrands } from 'api/brands-slice';
import {
  useBulkArchiveMutation,
  useBulkAssignCandidateMutation,
  useBulkDeletionMutation,
  useBulkExportMutation,
  useBulkRemindersMutation,
  useBulkTagDeletionMutation,
  useBulkTagMutation,
  useGetCandidatesCountQuery,
  useLazyGetCandidateRefereesQuery,
} from 'api/candidates-api';
import { deleteCandidate, getCandidates, updateCandidate } from 'api/candidates-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { getTags, selectTags } from 'api/tags-slice';
import { getTeams, selectTeams } from 'api/teams-slice';
import { useGetCompanyUsersQuery } from 'api/user-api';
import { selectPermissions } from 'api/user-permissions-slice';
import { selectUser } from 'api/user-slice';

import Alert from '../../components/Alert/Alert';
import styles from './Dashboard.module.scss';

// Actions cell component that disables buttons during bulk selection
const CandidateActionsCell = ({ candidate, onHistory, onArchive, onDelete }) => {
  const { selectedIds } = useTableContext();
  const isBulkActive = selectedIds.length > 0;

  return (
    <div className={cn('d-flex', 'align-items-center')}>
      <Link to={`/check/${candidate.id}/background_checks`}>
        <IconButton
          className="u-margin-right--small"
          icon={CheckmarkCircle02Icon}
          prefix="hugeicons"
          tip="View report"
          placement="top"
          disabled={isBulkActive}
        />
      </Link>
      <IconButton
        className="u-margin-right--small"
        icon={WorkHistoryIcon}
        prefix="hugeicons"
        tip="View history"
        onClick={() => onHistory(candidate.id)}
        placement="top"
        disabled={isBulkActive}
      />
      <IconButton
        className="u-margin-right--small"
        icon={candidate.archived ? RestoreBinIcon : Archive03Icon}
        prefix="hugeicons"
        tip={candidate.archived ? 'Restore' : 'Archive'}
        active={candidate.archived}
        onClick={() => onArchive(candidate)}
        placement="top"
        disabled={isBulkActive}
      />
      <IconButton
        icon={Delete02Icon}
        prefix="hugeicons"
        tip="Delete"
        onClick={() => onDelete(candidate.id)}
        placement="top"
        disabled={isBulkActive}
      />
    </div>
  );
};

/**
 * Format duration from seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '0 seconds';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes > 0 && days === 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);

  return parts.length > 0 ? parts.join(', ') : 'less than a minute';
};

/**
 * Format a large number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
const formatNumber = num => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

const Dashboard = () => {
  useDocumentTitle('Dashboard');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tags = useSelector(selectTags);
  const user = useSelector(selectUser);

  const permissions = useSelector(selectPermissions);
  const companyChecks = useSelector(selectCompanyChecks);
  const company = useSelector(selectCompany);
  const teams = useSelector(selectTeams);
  const brands = useSelector(selectBrands);
  const { dateSlashFormat } = useLocale(company?.locale);

  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: statusesCountResult, refetch: refetchStatusCounts } = useGetCandidatesCountQuery({
    refetchOnMountOrArgChange: true,
  });

  const [bulkReminders] = useBulkRemindersMutation();
  const [bulkTagDeletion] = useBulkTagDeletionMutation();
  const [bulkTag] = useBulkTagMutation();
  const [bulkArchive] = useBulkArchiveMutation();
  const [bulkdeletion] = useBulkDeletionMutation();
  const [bulkExport] = useBulkExportMutation();
  const [bulkAssignCandidate] = useBulkAssignCandidateMutation();
  const [getCandidateReferees] = useLazyGetCandidateRefereesQuery();

  const [loading, setLoading] = useState(false);
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });
  const [statusesCounts, setStatusesCounts] = useState({
    active: 0,
    completed: 0,
    archived: 0,
    overdue: 0,
    in_progress: 0,
    all: 0,
  });
  const [showOverflowFilters, setShowOverflowFilters] = useState(false);
  const [overflowFiltersVisible, setOverflowFiltersVisible] = useState(false);
  const [overflowFilterItems, setOverflowFilterItems] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showBrandDetails, setShowBrandDetails] = useState(false);
  const [showCandidateHistory, setShowCandidateHistory] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [referees, setReferees] = useState([]);

  const [alertDelete, setAlertDelete] = useState(false);
  const [alertParams, setAlertParams] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [useAllForBulk, setUseAllForBulk] = useState(false);
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);

  const hasAccess = useAccessControl(permissions, { notPermissionPolicy: 'allowed' });

  // Store dashboard filters in localStorage (stores full queryParams with names)
  // Only use localStorage when user ID is available to prevent key instability
  const localStorageKey = user?.id ? `dashboard_filters_${user.id}` : null;
  const [storedFilters, setStoredFilters] = useLocalStorage(localStorageKey, {
    page: { value: 1 },
  });

  const extractFlatParams = fullQueryParams => {
    const params = {};
    Object.keys(fullQueryParams || {})?.forEach(key => {
      const stored = fullQueryParams[key];
      if (stored && typeof stored === 'object' && 'value' in stored) {
        params[key] = stored.value;
      } else {
        params[key] = stored;
      }
    });
    return params;
  };

  // Use stored filters directly as defaultQuery (already in correct format with names)
  const defaultQuery = useMemo(() => {
    const result = {};
    Object.keys(storedFilters).forEach(key => {
      if (key === 'search') return;

      const stored = storedFilters[key];
      // Check if it's already in the new format (object with 'value' property)
      if (stored && typeof stored === 'object' && 'value' in stored) {
        result[key] = stored;
      } else {
        // Old format - convert to new format
        result[key] = { value: stored };
      }
    });
    return result;
  }, [storedFilters]);

  // Initialization
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Dashboard',
    });

    dispatch(getTags());
    dispatch(getTeams());
    dispatch(getBrands());
  }, []);

  // Status counts
  useEffect(() => {
    if (!statusesCountResult) return;
    setStatusesCounts(statusesCountResult.result);
  }, [statusesCountResult]);

  // Handle overflow filters based on screen size - simple and reliable
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const overflowItems = [];

      if (width < 1800) {
        overflowItems.push('assignedTo');
      }
      if (width < 1600) {
        overflowItems.push('brand');
      }
      if (width < 1400) {
        overflowItems.push('team');
      }
      if (width < 1200) {
        overflowItems.push('tags');
      }
      if (width < 1000) {
        overflowItems.push('role');
      }
      if (width < 800) {
        overflowItems.push('status');
      }
      if (width < 600) {
        overflowItems.push('completedWithin');
      }
      if (width < 400) {
        overflowItems.push('createdWithin');
      }

      setOverflowFilterItems(overflowItems);
      setShowOverflowFilters(overflowItems.length > 0);
    };

    handleResize(); // Call once on mount
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch candidates function
  const fetchCandidates = async (params = {}, records_per_page = null) => {
    // Only show loading overlay for subsequent fetches, not initial load
    // This prevents the full-screen loading flash on page load
    if (initialFetchComplete) {
      setLoading(true);
    }

    const records = records_per_page || params.records_per_page || pagination.records_per_page;
    const paramsWithLimit = { ...params, records };

    try {
      const { payload } = await dispatch(getCandidates({ params: paramsWithLimit }));
      const newPagination = {
        ...payload.result.pagination,
        records_per_page: records, // Preserve the requested page size
        companyTotalRecords: payload.result.pagination.total_records,
      };

      setPagination(newPagination);
      setCandidates(payload.result.candidates);

      // Only refetch status counts if this isn't the initial load
      // (status counts are fetched separately on mount)
      if (initialFetchComplete) {
        refetchStatusCounts();
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
      if (!initialFetchComplete) {
        setInitialFetchComplete(true);
      }
    }
  };

  // Wrapper function to save filters to localStorage when they change
  const handleQueryUpdate = (params, fullQueryParams) => {
    if (fullQueryParams) {
      setStoredFilters(fullQueryParams);
    }
    fetchCandidates(params);
  };

  // Handle page size change
  const handlePageSizeChange = newSize => {
    setPagination(prev => ({ ...prev, records_per_page: newSize, page: 1 }));
    fetchCandidates({ ...extractFlatParams(storedFilters), page: 1 }, newSize);
  };

  const handleCandidateHistory = async candidateId => {
    try {
      const candidate = candidates.find(c => c.id === candidateId);
      const refereesData = await getCandidateReferees(candidateId).unwrap();
      setSelectedCandidate(candidate);
      setReferees(refereesData);
      setShowCandidateHistory(true);
    } catch (error) {
      console.error('Error fetching candidate history:', error);
    }
  };

  const handleBulkReminders = async (ids = selectedIds, useAll = useAllForBulk) => {
    try {
      setLoading(true);
      await bulkReminders({ ids, useAll });
      toast.custom(t => (
        <CustomToast t={t} type="success" title="Success" message="Reminders sent successfully" />
      ));
      handleBulkToggle();
    } catch (error) {
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to send reminders. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async (ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      await bulkExport({ ids, useAll });
      handleBulkToggle();
    } catch (error) {
      dispatch(
        messagePopUp({ text: 'Something went wrong', state: MESSAGE_STATE_ERROR, hide: true }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeletion = async (ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      const { data: response } = await bulkdeletion({ ids, useAll });

      // Check if response exists and has result property
      if (response && response.result !== undefined) {
        toast.custom(t => (
          <CustomToast
            t={t}
            type="success"
            title="Success"
            message={`${response.result} candidates deleted successfully`}
          />
        ));
        await fetchCandidates(extractFlatParams(storedFilters));
        handleBulkToggle();
      } else {
        // Handle case where response doesn't have expected structure
        toast.custom(t => (
          <CustomToast
            t={t}
            type="error"
            title="Error"
            message="Unexpected response from server. Please try again."
          />
        ));
      }
    } catch (error) {
      console.error('Bulk deletion error:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to delete candidates. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArchive = async (ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      const { data: response } = await bulkArchive({ ids, useAll });

      // Check if response exists and has result property
      if (response && response.result !== undefined && response.result > 0) {
        toast.custom(t => (
          <CustomToast
            t={t}
            type="success"
            title="Success"
            message={`${response.result} candidates archived successfully`}
          />
        ));
        await fetchCandidates(extractFlatParams(storedFilters));
        handleBulkToggle();
      } else {
        // Handle case where response doesn't have expected structure
        toast.custom(t => (
          <CustomToast
            t={t}
            type="error"
            title="Error"
            message="Unexpected response from server. Please try again."
          />
        ));
      }
    } catch (error) {
      console.error('Bulk archive error:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to archive candidates. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkTag = async (tagId, ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      const { data: response } = await bulkTag({
        ids,
        tag_id: tagId,
        use_all: useAll,
      });

      // Check if response exists and has result property
      if (response && response.result !== null && response.result !== undefined) {
        if (response.result > 0) {
          toast.custom(t => (
            <CustomToast
              t={t}
              type="success"
              title="Success"
              message={`${response.result} candidates tagged successfully`}
            />
          ));
        }
        handleBulkToggle();
      } else {
        // Handle case where response doesn't have expected structure
        toast.custom(t => (
          <CustomToast
            t={t}
            type="error"
            title="Error"
            message="Unexpected response from server. Please try again."
          />
        ));
      }
    } catch (error) {
      console.error('Bulk tag error:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to tag candidates. Please try again."
        />
      ));
    } finally {
      await fetchCandidates(extractFlatParams(storedFilters));
      setLoading(false);
    }
  };

  const handleBulkAssignCandidate = async (userId, ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      const { data: response } = await bulkAssignCandidate({
        ids,
        user_id: userId.user_id,
        use_all: useAll,
      });
      toast.custom(t => (
        <CustomToast
          t={t}
          type="success"
          title="Success"
          message="Candidates re-assigned successfully"
        />
      ));
      await fetchCandidates(extractFlatParams(storedFilters));
      handleBulkToggle();
    } catch (error) {
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to re-assign candidates. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkTagDeletion = async (ids = selectedIds, useAll = useAllForBulk) => {
    setLoading(true);
    try {
      const { data: response } = await bulkTagDeletion({ ids, useAll });

      // Check if response exists and has result property
      if (response && response.result !== undefined) {
        if (response.result > 0) {
          toast.custom(t => (
            <CustomToast
              t={t}
              type="success"
              title="Success"
              message={`${response.result} tags removed successfully`}
            />
          ));
        }
        handleBulkToggle();
      } else {
        // Handle case where response doesn't have expected structure
        toast.custom(t => (
          <CustomToast
            t={t}
            type="error"
            title="Error"
            message="Unexpected response from server. Please try again."
          />
        ));
      }
    } catch (error) {
      console.error('Bulk tag deletion error:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to remove tags. Please try again."
        />
      ));
    } finally {
      await fetchCandidates(extractFlatParams(storedFilters));
      setLoading(false);
    }
  };

  const onCheckDelete = async params => {
    setAlertDelete(false);
    setLoading(true);
    try {
      await dispatch(deleteCandidate(params.candidate_id));
      await fetchCandidates(extractFlatParams(storedFilters));
      toast.custom(t => (
        <CustomToast
          t={t}
          type="success"
          title="Success"
          message="Candidate deleted successfully"
        />
      ));
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to delete candidate. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const onCheckDeleteConfirmation = candidate_id => {
    setAlertDelete(true);
    setAlertParams({ candidate_id });
  };

  const onArchivedStatusChange = async candidate => {
    setLoading(true);
    try {
      await dispatch(
        updateCandidate({ id: candidate.id, params: { archived: !candidate.archived } }),
      );
      await fetchCandidates(extractFlatParams(storedFilters));
      toast.custom(t => (
        <CustomToast
          t={t}
          type="success"
          title="Success"
          message={`Candidate ${candidate.archived ? 'restored' : 'archived'} successfully`}
        />
      ));
    } catch (error) {
      console.error('Error updating candidate archive status:', error);
      toast.custom(t => (
        <CustomToast
          t={t}
          type="error"
          title="Error"
          message="Failed to update candidate status. Please try again."
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for rendering status icons
  const subStatus = (check_status, referee_status = null) => {
    let status = `(${check_status.status.capitalize()}`;

    if (referee_status?.status === 'in-progress') {
      status += ` - ${check_status.in_progress_sub_status?.replaceAll('_', ' ').capitalize()}`;
    } else if (check_status.status === 'submitted' || check_status.status === 'in-progress') {
      status += ` - ${check_status.in_progress_sub_status?.replaceAll('_', ' ').capitalize()}`;
    } else if (
      check_status.status === 'completed' &&
      check_status.user_review_status !== 'add_review'
    ) {
      status += ` - ${check_status.user_review_status?.replaceAll('_', ' ').capitalize()}`;
    }

    status += ')';
    return status;
  };

  const userReviewIcon = (user_review_status, check_status) => {
    if (check_status !== 'completed') {
      return null;
    }

    switch (user_review_status) {
      case 'approved':
        return (
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={16}
            strokeWidth={2}
            className={cn('white-text', 'u-absolute', 'surface-success', 'u-border-radius--half')}
            style={{ top: '-10px', right: '-8px', zIndex: 10, padding: '1px' }}
          />
        );
      case 'not_approved':
        return (
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={16}
            strokeWidth={2}
            className={cn('white-text', 'u-absolute', 'surface-error', 'u-border-radius--half')}
            style={{ top: '-10px', right: '-8px', zIndex: 10, padding: '1px' }}
          />
        );
      case 'needs_review':
        return (
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={16}
            strokeWidth={2}
            className={cn('white-text', 'u-absolute', 'surface-warning', 'u-border-radius--half')}
            style={{ top: '-10px', right: '-8px', zIndex: 10, padding: '1px' }}
          />
        );
      default:
        return null;
    }
  };

  const renderStatusIcons = candidate => {
    let icons = [];

    candidate.checks_statuses?.forEach(check_status => {
      const checkType = companyChecks?.find(
        companyCheck => companyCheck.type === check_status.type,
      )?.check_type;
      if (!checkType) return;

      if (
        (check_status.type === 'reference' || check_status.type === 'custom_reference') &&
        candidate.referees_statuses
      ) {
        candidate.referees_statuses.forEach(referee => {
          icons.push(
            <OverlayTrigger
              key={referee.id}
              overlay={
                <Tooltip id={referee.id}>
                  {referee.name} Referee{' '}
                  {check_status?.status === 'canceled'
                    ? `(${t('common.canceled')})`
                    : subStatus(check_status, referee)}{' '}
                </Tooltip>
              }>
              <div
                className={cn('position-relative', 'd-inline-block')}
                style={{ height: '16px', width: '16px', verticalAlign: 'middle' }}>
                {referee.warning_flag === 1 && referee.status === 'completed' ? (
                  <HugeiconsIcon
                    icon={AlertCircleIcon}
                    size={14}
                    strokeWidth={2}
                    className={cn(
                      'white-text',
                      'u-absolute',
                      'surface-error',
                      'u-border-radius--half',
                    )}
                    style={{
                      top: '-10px',
                      right: '-8px',
                      zIndex: 10,
                      padding: '1px',
                    }}
                  />
                ) : (
                  userReviewIcon(check_status.user_review_status, check_status.status)
                )}
                <Link
                  to={`/check/${candidate.id}/${checkType.candidate_tab}/${referee.id}`}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'block',
                    zIndex: 1,
                  }}>
                  <HugeiconsIcon
                    icon={UserIcon}
                    size={20}
                    strokeWidth={2}
                    className={`u-txt-status-${check_status?.status === 'canceled' ? check_status?.status : referee.status}`}
                  />
                </Link>
              </div>
            </OverlayTrigger>,
          );
        });
      } else {
        icons.push(
          <OverlayTrigger
            key={check_status.id}
            overlay={
              <Tooltip id={check_status.id}>
                {checkType.title} {subStatus(check_status)}{' '}
                {check_status.result_flag === 1 && check_status.status === 'completed'
                  ? ' - This check has results'
                  : ''}
              </Tooltip>
            }>
            <div
              className={cn('position-relative', 'd-inline-block')}
              style={{ height: '16px', width: '16px', verticalAlign: 'middle' }}>
              {check_status.result_flag === 1 && check_status.status === 'completed' ? (
                <HugeiconsIcon
                  icon={AttachmentCircleIcon}
                  size={16}
                  strokeWidth={2}
                  className={cn(
                    'white-text',
                    'u-absolute',
                    'surface-error',
                    'u-border-radius--half',
                  )}
                  style={{ top: '-10px', right: '-8px', zIndex: 10, padding: '1px' }}
                />
              ) : (
                userReviewIcon(check_status.user_review_status, check_status.status)
              )}
              <Link
                to={`/check/${candidate.id}/${checkType.candidate_tab}`}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'block',
                  zIndex: 1,
                }}>
                <HugeiconsIcon
                  icon={getCheckTypeIcon(check_status.type)}
                  size={20}
                  strokeWidth={2}
                  className={`u-txt-status-${check_status.status}`}
                />
              </Link>
            </div>
          </OverlayTrigger>,
        );
      }
    });

    return icons;
  };

  // Filter options
  const getStatusOptions = () => [
    {
      name: `All (${statusesCounts.all})`,
      value: 'completed,overdue,in-progress,candidate,archived',
    },
    { name: `Active (${statusesCounts.active})`, value: 'completed,in-progress,overdue,candidate' },
    { name: `Candidate (${statusesCounts.candidate})`, value: 'candidate' },
    { name: `In Progress (${statusesCounts.in_progress})`, value: 'in-progress' },
    { name: `Completed (${statusesCounts.completed})`, value: 'completed' },
    { name: `Overdue (${statusesCounts.overdue})`, value: 'overdue' },
    { name: `Archived (${statusesCounts.archived})`, value: 'archived' },
  ];

  const getTagOptions = () =>
    (tags || [])
      ?.map(tag => ({ name: tag.name, value: tag.id }))
      ?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [];

  const getTeamOptions = () =>
    (teams || [])
      ?.map(team => ({ name: team.name, value: team.id }))
      ?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [];

  const getBrandOptions = () =>
    (brands || [])
      ?.map(brand => ({ name: brand.name, value: brand.id }))
      ?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [];

  const roleOptions = useMemo(
    () =>
      Array.from(new Set(candidates?.map(c => c?.job_role)?.filter(Boolean)))
        .map(role => ({
          name: role,
          value: role,
        }))
        ?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [],
    [candidates],
  );

  const getUserOptions = () => {
    if (!companyUsers) return [];
    return companyUsers
      .map(user => ({ name: `${user.first_name} ${user.last_name}`, value: user.id }))
      .sort((a, b) => a?.name?.localeCompare(b?.name));
  };

  const getBrandName = brandId => brands?.find(brand => brand.id === brandId)?.name || 'Default';

  const handleBulkToggle = (selected = false) => {
    if (!selected) {
      setSelectedIds([]);
      setUseAllForBulk(false);
    }
  };

  // Empty state component that can access table context to clear filters
  const EmptyStateWithClearFilters = () => {
    const { clearQueryParams } = useTableContext();

    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center u-border-top"
        style={{
          minHeight: '400px',
          padding: '80px 20px',
        }}>
        <div className="mb-4">
          <IconButton
            icon="search"
            size="large"
            className="text-muted"
            style={{
              fontSize: '64px',
              opacity: 0.4,
              color: '#6c757d',
            }}
          />
        </div>
        <h4 className="text-muted mb-3" style={{ fontWeight: '500' }}>
          No candidates found
        </h4>
        <p className="text-muted mb-4 text-center" style={{ lineHeight: '1.5' }}>
          Try adjusting your filters or search terms to find what you're looking for.
        </p>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => {
            // Use the table context's clearQueryParams method to clear all filters
            // This will clear both the visual filter chips and reset the data
            clearQueryParams();
          }}>
          Clear all filters
        </Button>
      </div>
    );
  };

  // Bridge component to connect Table selection with BulkActionsBar
  const BulkActionsBarBridge = () => {
    const {
      selectedIds: tableSelectedIds,
      selectedAll,
      bulkUseAll,
      setSelectedIds,
      setSelectedAll,
      setBulkUseAll,
    } = useTableContext();

    // Sync the table's selection state with our local state
    React.useEffect(() => {
      setSelectedIds(tableSelectedIds);
      setUseAllForBulk(bulkUseAll);
    }, [tableSelectedIds, bulkUseAll]);

    const candidateTags = useMemo(() => {
      return candidates?.filter(
        candidate => tableSelectedIds.includes(candidate.id) && candidate.tags?.length > 0,
      );
    }, [candidates, tableSelectedIds]);

    // Enhanced close handler that clears both local and table state
    const handleClose = () => {
      setSelectedIds([]);
      setUseAllForBulk(false);
      setSelectedAll(false);
      setBulkUseAll(false);
    };

    return (
      <BulkActionsBar
        active={tableSelectedIds.length > 0}
        selectedIds={tableSelectedIds}
        onUseAllChange={setUseAllForBulk}
        tags={tags}
        candidateTags={candidateTags}
        users={companyUsers}
        reminderAction={() => handleBulkReminders(tableSelectedIds, bulkUseAll)}
        downloadCSV={() => handleDownloadCSV(tableSelectedIds, bulkUseAll)}
        bulkDeletion={async () => {
          await handleBulkDeletion(tableSelectedIds, bulkUseAll);
          handleClose();
        }}
        bulkArchive={() => handleBulkArchive(tableSelectedIds, bulkUseAll)}
        bulkTag={tagId => handleBulkTag(tagId, tableSelectedIds, bulkUseAll)}
        bulkTagDeletion={() => handleBulkTagDeletion(tableSelectedIds, bulkUseAll)}
        bulkAssignCandidate={userId =>
          handleBulkAssignCandidate(userId, tableSelectedIds, bulkUseAll)
        }
        handleClose={handleClose}
      />
    );
  };

  return (
    <AppPage loading={loading}>
      <BrandDetails
        brand={selectedBrand}
        show={showBrandDetails}
        onClose={() => setShowBrandDetails(false)}
      />
      <CandidateHistory
        candidate={selectedCandidate}
        company={company}
        referees={referees}
        user={user}
        companyChecks={companyChecks}
        show={showCandidateHistory}
        onClose={() => {
          setShowCandidateHistory(false);
        }}
      />
      <Alert
        show={alertDelete}
        title="Are you sure you want to delete this candidate permanently?"
        message="Deletion cannot be undone. Are you sure you want to continue?"
        ok="Delete"
        onOk={params => {
          setAlertDelete(false);
          onCheckDelete(params);
        }}
        cancel="Cancel"
        icon={Delete02Icon}
        showConfirmationInput
        type="error"
        onCancel={() => setAlertDelete(false)}
        params={alertParams}
      />

      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        <AppPage.Header
          title="Dashboard"
          icon={DashboardSquare01Icon}
          subtitle="Manage your candidates and pre-employment screening checks."
        />
      </div>

      {/* Show skeleton while waiting for user to load - BEFORE mounting Table.Context */}
      {!user?.id ? null : (
        // Only render Table.Context AFTER user.id is available
        // This ensures defaultQuery has the correct user-specific stored filters
        <Table.Context
          onQueryUpdate={handleQueryUpdate}
          defaultQuery={defaultQuery}
          onClearQueryParams={() => setStoredFilters({})}>
          {!initialFetchComplete ? (
            // Show skeleton while waiting for initial data fetch
            <div className="d-flex flex-column u-gap-large u-padding--large">
              <div className={styles.skeletonFilters}>
                <div className={styles.skeletonItem} style={{ width: '100px', height: '40px' }} />
                <div className={styles.skeletonItem} style={{ width: '80px', height: '40px' }} />
                <div className={styles.skeletonItem} style={{ width: '90px', height: '40px' }} />
              </div>
              <div className={styles.skeletonTable}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className={styles.skeletonRow}>
                    <div className={styles.skeletonItem} style={{ width: '60%', height: '20px' }} />
                    <div className={styles.skeletonItem} style={{ width: '20%', height: '20px' }} />
                    <div className={styles.skeletonItem} style={{ width: '15%', height: '20px' }} />
                  </div>
                ))}
              </div>
            </div>
          ) : pagination.total_records === 0 && statusesCounts.all === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center u-gap-xxx-large"
              style={{ minHeight: '60vh', padding: '4rem 2rem' }}>
              <div className="d-flex flex-column u-gap-medium">
                <span className="u-font-size-large u-font-weight-500">
                  👋 Hi, get started by adding a candidate
                </span>
                <Button
                  icon="user-plus"
                  onClick={() => navigate('/new_check')}
                  className="u-flex-align-self-center">
                  Add Candidate
                </Button>
              </div>
              <KnowledgeBaseCards />
            </div>
          ) : (
            <>
              <BulkActionsBarBridge />

              <Table.FloatingFilters className="u-margin--large surface-white">
                <Table.FloatingFiltersLeft>
                  {/* Always visible filters */}
                  {!overflowFilterItems.includes('status') && (
                    <Table.MultiSelectFilter
                      title="Status"
                      filterName="status"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={getStatusOptions()}
                    />
                  )}
                  {!overflowFilterItems.includes('tags') && (
                    <Table.MultiSelectFilter
                      title="Tags"
                      filterName="tag"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={getTagOptions()}
                    />
                  )}

                  {/* Conditionally visible filters */}
                  {!overflowFilterItems.includes('team') && (
                    <Table.MultiSelectFilter
                      title="Team"
                      filterName="team"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={getTeamOptions()}
                    />
                  )}
                  {!overflowFilterItems.includes('brand') && (
                    <Table.MultiSelectFilter
                      title="Brand"
                      filterName="brand"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={getBrandOptions()}
                    />
                  )}
                  {!overflowFilterItems.includes('role') && (
                    <Table.MultiSelectFilter
                      title="Role"
                      filterName="role"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={roleOptions}
                    />
                  )}
                  {!overflowFilterItems.includes('assignedTo') && (
                    <Table.MultiSelectFilter
                      title="Assigned To"
                      filterName="user"
                      type="status"
                      useDefault={false}
                      useSearchInMultiSelect={true}
                      options={getUserOptions()}
                    />
                  )}

                  {/* Date range filters at the end */}
                  {!overflowFilterItems.includes('createdWithin') && (
                    <Table.DateRangeFilter title="Created within" filterName="created_at" />
                  )}
                  {!overflowFilterItems.includes('completedWithin') && (
                    <Table.DateRangeFilter title="Completed within" filterName="completed_at" />
                  )}

                  {/* Overflow button */}
                  {showOverflowFilters && (
                    <Dropdown
                      show={overflowFiltersVisible}
                      onToggle={isOpen => setOverflowFiltersVisible(isOpen)}
                      align="end">
                      <Dropdown.Toggle
                        as="div"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          marginLeft: '15px',
                          height: '40px',
                        }}>
                        <IconButton
                          icon="more-horizontal"
                          tip="More Filters"
                          onClick={() => setOverflowFiltersVisible(!overflowFiltersVisible)}
                          style={{
                            height: '36px !important',
                            width: '36px !important',
                            display: 'flex !important',
                            alignItems: 'center !important',
                            justifyContent: 'center !important',
                            padding: '0 !important',
                            minWidth: '36px !important',
                          }}
                        />
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="custom-more-filters-menu"
                        style={{
                          minWidth: '240px !important',
                          padding: '16px 16px 16px 0 !important',
                          border: '1px solid #e5e7eb !important',
                          borderRadius: '12px !important',
                          boxShadow:
                            '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05) !important',
                          backgroundColor: 'white !important',
                          marginTop: '8px !important',
                          marginLeft: '15px !important',
                          transform: 'translateY(0) !important',
                          position: 'absolute !important',
                          top: '100% !important',
                          left: '0 !important',
                          right: 'auto !important',
                          zIndex: '1050 !important',
                        }}>
                        <div
                          style={{
                            marginBottom: '16px',
                            marginLeft: '15px',
                            fontWeight: '600',
                            color: '#111827',
                            fontSize: '14px',
                            letterSpacing: '-0.025em',
                          }}>
                          More Filters
                        </div>

                        {overflowFilterItems.includes('createdWithin') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.DateRangeFilter title="Created within" filterName="created_at" />
                          </div>
                        )}

                        {overflowFilterItems.includes('completedWithin') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.DateRangeFilter
                              title="Completed within"
                              filterName="completed_at"
                            />
                          </div>
                        )}

                        {overflowFilterItems.includes('status') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.MultiSelectFilter
                              title="Status"
                              filterName="status"
                              type="status"
                              useDefault={false}
                              useSearchInMultiSelect={true}
                              options={getStatusOptions()}
                            />
                          </div>
                        )}

                        {overflowFilterItems.includes('tags') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.MultiSelectFilter
                              title="Tags"
                              filterName="tag"
                              type="status"
                              useDefault={false}
                              useSearchInMultiSelect={true}
                              options={getTagOptions()}
                            />
                          </div>
                        )}

                        {overflowFilterItems.includes('team') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.MultiSelectFilter
                              title="Team"
                              filterName="team"
                              type="status"
                              useDefault={false}
                              useSearchInMultiSelect={true}
                              options={getTeamOptions()}
                            />
                          </div>
                        )}

                        {overflowFilterItems.includes('brand') && (
                          <div style={{ marginBottom: '16px' }}>
                            <Table.MultiSelectFilter
                              title="Brand"
                              filterName="brand"
                              type="status"
                              useDefault={false}
                              useSearchInMultiSelect={true}
                              options={getBrandOptions()}
                            />
                          </div>
                        )}

                        {overflowFilterItems.includes('assignedTo') && (
                          <div>
                            <Table.MultiSelectFilter
                              title="Assigned To"
                              filterName="user"
                              type="status"
                              useDefault={false}
                              useSearchInMultiSelect={true}
                              options={getUserOptions()}
                            />
                          </div>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </Table.FloatingFiltersLeft>
                <Table.FloatingFiltersRight>
                  {hasAccess(Permissions.CandidateWrite) && (
                    <Button
                      dataTestId="new-check-button"
                      icon="user-plus"
                      onClick={() => navigate('/new_check')}>
                      Add Candidate
                    </Button>
                  )}
                </Table.FloatingFiltersRight>
              </Table.FloatingFilters>

              <Table.TableContent className="u-margin--large">
                <Table.Filters>
                  <Table.Search placeholder="Search for a candidate..." regex={/[^a-zA-Z0-9\s@+.]/g} />
                </Table.Filters>

                {pagination.total_records === 0 && statusesCounts.all > 0 ? (
                  // Empty state for filtered results - outside table structure
                  <EmptyStateWithClearFilters />
                ) : (
                  <Table>
                    <Table.Head>
                      <Table.Row>
                        <Table.Header bulkSelector style={{ paddingRight: '8px' }}></Table.Header>
                        <Table.Header sortKey="candidates.status">Status</Table.Header>
                        <Table.Header sortKey="candidates.first_name">Candidate Name</Table.Header>
                        <Table.Header>Check Types</Table.Header>
                        <Table.Header
                          sortKey="candidates.job_role"
                          className="hide-on-mobile-landscape">
                          Role
                        </Table.Header>
                        <Table.Header sortKey="candidates.department" className="hide-on-tablet">
                          Department
                        </Table.Header>
                        <Table.Header sortKey="users.name" className="hide-on-large-tablet">
                          Assigned To
                        </Table.Header>
                        <Table.Header className="hide-on-large-tablet">Brand</Table.Header>
                        <Table.Header
                          sortKey="candidates.created_at"
                          className="hide-on-mobile-landscape">
                          Created
                        </Table.Header>
                        <Table.Header>Actions</Table.Header>
                      </Table.Row>
                    </Table.Head>
                    <Table.Body>
                      {candidates.map(candidate => (
                        <Table.Row key={candidate.id}>
                          <Table.Col
                            bulkSelector
                            selectorId={candidate.id}
                            style={{ paddingRight: '8px' }}></Table.Col>
                          <Table.Col>
                            <Badge className={`u-status-${candidate.status}`}>
                              {candidate.status}
                            </Badge>
                          </Table.Col>
                          <Table.Col>
                            <div className={cn('d-flex', 'align-items-center', 'u-gap')}>
                              <div className="d-flex flex-column u-gap-small">
                                <Link
                                  to={`/check/${candidate.id}`}
                                  className="u-link dark u-margin-right--small u-text-ellipsis u-max-width-x-small">
                                  {candidate.name}
                                </Link>
                                <span className="text-muted small u-no-margin u-text-ellipsis u-max-width-x-small">
                                  {candidate.reference_code && candidate.reference_code}
                                </span>
                              </div>
                              {candidate.tags?.length > 0 && (
                                <RolloverTooltip
                                  type="white"
                                  tooltip={
                                    <>
                                      {candidate.tags.map(tag => (
                                        <div key={tag.id} className="tooltip-tag-pill">
                                          {tag.name}
                                        </div>
                                      ))}
                                    </>
                                  }>
                                  <StatusPill
                                    status="canceled"
                                    text={
                                      <>
                                        <HugeiconsIcon icon={Tag01Icon} size={20} />
                                        <span className="u-margin-left--small">
                                          {candidate.tags.length}
                                        </span>
                                      </>
                                    }
                                  />
                                </RolloverTooltip>
                              )}
                            </div>
                          </Table.Col>
                          <Table.Col>
                            <div
                              className={cn(
                                'd-inline-flex',
                                'align-items-center',
                                'flex-wrap',
                                'u-gap-mid-small',
                              )}
                              style={{ verticalAlign: 'middle' }}>
                              {renderStatusIcons(candidate)}
                            </div>
                          </Table.Col>
                          <Table.Col className="hide-on-mobile-landscape">
                            <p className="u-text-ellipsis u-no-margin u-max-width-x-small">
                              {candidate.job_role || '-'}
                            </p>
                          </Table.Col>
                          <Table.Col className="hide-on-tablet">
                            <p className="u-text-ellipsis u-no-margin u-max-width-x-small">
                              {candidate.department || '-'}
                            </p>
                          </Table.Col>
                          <Table.Col className="hide-on-large-tablet">
                            <p className="u-text-ellipsis u-no-margin u-max-width-x-small">
                              {candidate?.user_name}
                            </p>
                          </Table.Col>
                          <Table.Col className="hide-on-large-tablet">
                            <a
                              className="u-text-ellipsis u-no-margin u-link u-max-width-x-small"
                              onClick={() => {
                                setSelectedBrand(
                                  brands?.find(brand => brand.id === candidate.brand_id),
                                );
                                setShowBrandDetails(true);
                              }}>
                              {getBrandName(candidate.brand_id)}
                            </a>
                          </Table.Col>
                          <Table.Col className="hide-on-mobile-landscape">
                            <div className="d-flex flex-column">
                              <Moment format={dateSlashFormat}>{candidate.created_at}</Moment>
                              <span className="text-muted small">
                                <Moment fromNow>{candidate.created_at}</Moment>
                              </span>
                            </div>
                          </Table.Col>
                          <Table.Col>
                            <CandidateActionsCell
                              candidate={candidate}
                              onHistory={handleCandidateHistory}
                              onArchive={onArchivedStatusChange}
                              onDelete={onCheckDeleteConfirmation}
                            />
                          </Table.Col>
                        </Table.Row>
                      ))}
                    </Table.Body>
                    <Table.Pagination {...pagination} onPageSizeChange={handlePageSizeChange} />
                  </Table>
                )}
              </Table.TableContent>
            </>
          )}
        </Table.Context>
      )}
    </AppPage>
  );
};

export default Dashboard;
